import jwtDecode from "jwt-decode";
import { MainLayout, MainWrapper } from "layouts";
import { useCallback, useEffect, useRef, useState } from "react";
import { setSessionValues } from "utilz/helpers/apiRequestAuthHelper";
import { PermissionProvider } from "controller/context/PermissionContext";
import { useLogto } from "@logto/react";
import { useSetInterceptorToken } from "utilz";
import { useNavigate } from "react-router-dom";

type DecodedToken = {
  tenantId?: string;
  legacyTenantId?: string;
  legacyUserId?: string;
  sub?: string;
  email?: string;
  roles?: string[];
  permissions?: string[];
};

export const SecureWrapper = () => {
  const [] = useSetInterceptorToken();
  // One-way flag: flips true after the first successful session bootstrap and
  // stays true. Silent token refreshes NEVER unmount the app tree afterwards.
  const [isBootstrapped, setIsBootstrapped] = useState(false);
  const hasBootstrapped = useRef(false);
  const navigate = useNavigate();
  const { signOut, isAuthenticated, getAccessToken, isLoading, clearAccessToken } =
    useLogto();

  const bootstrap = useCallback(
    async (attempt = 0) => {
      try {
        const token = await getAccessToken(import.meta.env.VITE_APP_BASE_URL);
        if (!token) {
          signOut(`${window.location.origin}/login`);
          return;
        }
        const decodedData = jwtDecode<DecodedToken>(token);
        if (!decodedData?.tenantId) {
          // Cap the retry so a token that never carries tenantId can't loop
          // forever and exhaust the refresh token.
          if (attempt >= 1) {
            signOut(`${window.location.origin}/login`);
            return;
          }
          await clearAccessToken();
          await bootstrap(attempt + 1);
          return;
        }
        const decodedObj = {
          accessToken: token,
          tenantId: decodedData?.legacyTenantId || decodedData?.tenantId,
          sub: decodedData?.legacyUserId || decodedData?.sub,
          email: decodedData?.email,
          roles: decodedData?.roles || [],
          permissions: decodedData?.permissions || [],
        };

        setSessionValues(decodedObj.accessToken, decodedObj);
        setIsBootstrapped(true);
      } catch {
        signOut(`${window.location.origin}/login`);
      }
    },
    [getAccessToken, clearAccessToken, signOut]
  );

  useEffect(() => {
    // Wait for Logto to settle before deciding anything.
    if (isLoading) {
      return;
    }
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }
    // Bootstrap the session exactly once. The axios request interceptor
    // attaches a fresh token per request, so we must NOT re-run this on every
    // isLoading toggle (which is what caused the full-app remount on expiry).
    if (!hasBootstrapped.current) {
      hasBootstrapped.current = true;
      bootstrap();
    }
  }, [isAuthenticated, isLoading, bootstrap, navigate]);

  // Block ONLY until the first successful bootstrap — never on later refreshes.
  if (!(isAuthenticated && isBootstrapped)) {
    return null;
  }
  return (
    <MainWrapper>
      <PermissionProvider>
        <MainLayout />
      </PermissionProvider>
    </MainWrapper>
  );
};
